import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Button,
  Divider,
  Group,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { post } from "aws-amplify/api";
import { COLORS } from "@/utils/GlobalStyles";
import { UserState } from "@/utils/UserProvider";
import { ESSENTIAL_LIMITS, FREE_LIMITS } from "@/utils/checkSubscriptionUsage";
import { LOOKUP_KEYS } from "@/utils/utils";
import PlanCard from "./PlanCard";
import SubscribeWhilePromotionalConfirmationModal from "./SubscribeWhilePromotionalConfirmationModal";
import SwitchPlanConfirmationModal from "./SwitchPlanConfirmationModal";

interface PlanFeaturesProps {
  hideFreePlan: boolean;
  userId: string;
  setOpenedModalCancel: (value: string) => void;
  amplifyUser: any;
  activeStripeSubscription: any;
  scheduledSubscriptions: any;
  products: any;
  prices: any;
  revcatUser: any;
  userSubscriptions: any;
  initStripe: (silent?: boolean) => Promise<void>;
  initRevcat: () => Promise<UserState | null | undefined>;
  upcomingInvoice: any;
  setUpcomingInvoiceModal: (value: boolean) => void;
  revokeRevcatPromotion: () => Promise<void>;
}

const PlanFeatures = ({
  hideFreePlan,
  userId,
  setOpenedModalCancel,
  amplifyUser,
  activeStripeSubscription,
  scheduledSubscriptions,
  prices,
  initStripe,
  initRevcat,
  revcatUser,
  userSubscriptions,
  upcomingInvoice,
  setUpcomingInvoiceModal,
  products,
  revokeRevcatPromotion,
}: PlanFeaturesProps) => {
  const navigate = useNavigate();
  const [cancellingSchedule, setCancellingSchedule] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<
    "monthly" | "yearly"
  >("monthly");
  const [switchPlanConfirmModalData, setSwitchPlanConfirmModalData] =
    useState<any>(null);
  const [
    subscribeWhilePromotionalConfirmModalData,
    setSubscribeWhilePromotionalConfirmModalData,
  ] = useState<any>(null);

  useEffect(() => {
    if (activeStripeSubscription) {
      setSubscriptionType(
        activeStripeSubscription.plan.interval === "year"
          ? "yearly"
          : "monthly",
      );
    }
  }, [activeStripeSubscription]);

  const renderScheduledSubscriptionText = (lookupKey: string) => {
    if (scheduledSubscriptions.length === 0) {
      return;
    }
    const scheduledSubscription = scheduledSubscriptions[0];
    const priceId = scheduledSubscription.phases[0].items[0].price;
    const price = prices.find((price: any) => price.id === priceId);
    if (price?.lookup_key !== lookupKey) {
      return;
    }
    return (
      <Stack gap={0}>
        <Text size="md" c={"grey"} fw={600}>
          Scheduled to subscribe on:{" "}
        </Text>
        <Text size="md" c={"grey"} fw={600}>
          {moment(scheduledSubscription.phases[0].start_date * 1000).format(
            "MM/DD/YYYY, hh:mm A",
          )}
        </Text>
      </Stack>
    );
  };

  const renderExtraTexts = (lookupKey: string) => {
    const activePromotion = getActivePromotional(lookupKey);
    if (activePromotion) {
      return (
        <Text size="md" c={COLORS.primary} fw={600}>
          Trailing until:{" "}
          {moment(activePromotion.expirationDate).format("MM/DD/YYYY, hh:mm A")}
        </Text>
      );
    }

    if (!activeStripeSubscription) {
      return null;
    }

    if (!isCurrentPlan(lookupKey)) {
      return null;
    }

    if (
      activeStripeSubscription.current_period_end < Date.now() / 1000 ||
      (activeStripeSubscription.status === "trialing" &&
        activeStripeSubscription.trial_end < Date.now() / 1000)
    ) {
      return null;
    }

    const planInterval = activeStripeSubscription?.plan?.interval;

    if (planInterval === "year" && subscriptionType === "monthly") {
      return null;
    }

    if (planInterval === "month" && subscriptionType === "yearly") {
      return null;
    }

    if (activeStripeSubscription.status === "trialing") {
      return (
        <Text size="md" c={COLORS.primary} fw={600}>
          Trailing until:{" "}
          {moment(activeStripeSubscription.current_period_end * 1000).format(
            "MM/DD/YYYY, hh:mm A",
          )}
        </Text>
      );
    }
    if (!activeStripeSubscription.cancel_at_period_end) {
      return (
        <Text size="md" c={COLORS.primary} fw={600}>
          Next invoice:{" "}
          {moment(activeStripeSubscription.current_period_end * 1000).format(
            "MM/DD/YYYY, hh:mm A",
          )}
        </Text>
      );
    }
    if (activeStripeSubscription.cancel_at_period_end) {
      return (
        <Stack gap={0}>
          <Text size="md" c={"red"} fw={600}>
            Canceled at:{" "}
            {moment(activeStripeSubscription.canceled_at * 1000).format(
              "MM/DD/YYYY, hh:mm A",
            )}
          </Text>
          <Text size="md" c={COLORS.primary} fw={600}>
            Expires at:{" "}
            {moment(activeStripeSubscription.current_period_end * 1000).format(
              "MM/DD/YYYY, hh:mm A",
            )}
          </Text>
        </Stack>
      );
    }
    return null;
  };

  const getActivePromotional = (lookupKey: string) => {
    const activePromotion = (revcatUser as any).customerInfoApi?.entitlements
      .active[lookupKey.includes("premium") ? "Premium" : "Essential"];
    return activePromotion && activePromotion?.store === "promotional"
      ? activePromotion
      : null;
  };

  const isCurrentPlan = (lookupKey: string) => {
    if (!!getActivePromotional(lookupKey)) {
      return true;
    }
    if (!activeStripeSubscription) {
      return false;
    }
    const priceId = prices.find(
      (price: any) => price.lookup_key === lookupKey,
    ).id;
    return activeStripeSubscription?.plan?.id === priceId;
  };

  const checkHasUpcomingInvoice = (lookupKey: string) => {
    if (!upcomingInvoice) {
      return false;
    }
    const priceId = prices.find(
      (price: any) => price.lookup_key === lookupKey,
    ).id;
    return upcomingInvoice?.subscription?.plan?.id === priceId;
  };

  const handleSubscribe = async (
    lookupKey: string,
    force?: boolean,
    extraParams?: any,
  ) => {
    setSwitchPlanConfirmModalData(null);
    const plan = lookupKey?.split("_")[1].toUpperCase();
    const priceId = prices.find(
      (price: any) => price.lookup_key === lookupKey,
    ).id;
    if (!priceId) {
      return;
    }
    const activePromotional =
      getActivePromotional(premiumLookupKey) ||
      getActivePromotional(essentialLookupKey);
    if (activePromotional && !force) {
      setSubscribeWhilePromotionalConfirmModalData({
        lookupKey,
        activePromotional,
      });
      return;
    }
    if (
      activeStripeSubscription &&
      !activeStripeSubscription.cancel_at_period_end
    ) {
      if (!force) {
        setSwitchPlanConfirmModalData({
          priceId,
          customerId: activeStripeSubscription.customer,
          subscriptionId: activeStripeSubscription.id,
          lookupKey,
        });
        return;
      }
      await handleUpdateSubscription(activeStripeSubscription.id, priceId);
      return;
    }
    if (
      activeStripeSubscription &&
      activeStripeSubscription.cancel_at_period_end &&
      priceId !== activeStripeSubscription.plan.id
    ) {
      await handleUpdateSubscription(activeStripeSubscription.id, priceId);
      return;
    }
    if (
      activeStripeSubscription &&
      activeStripeSubscription?.cancel_at_period_end &&
      priceId === activeStripeSubscription.plan.id
    ) {
      await handleResubscribeSubscription(
        activeStripeSubscription.customer,
        activeStripeSubscription.id,
      );
      return;
    }
    setSubscribing(true);
    if (extraParams?.revokePromotion) {
      await revokeRevcatPromotion();
    }
    if (extraParams) {
      delete extraParams?.revokePromotion;
    }
    navigate("/checkout", {
      state: {
        plan,
        userId,
        lookup_key: lookupKey,
        email: amplifyUser.email,
        stripeCustomerId:
          activeStripeSubscription?.customer || userSubscriptions[0]?.customer,
        ...(extraParams || {}),
      },
    });
  };

  const handleUpdateSubscription = async (
    subscriptionId: string,
    priceId: string,
  ) => {
    setSubscribing(true);
    try {
      const updateSubscription = post({
        apiName: "StripeBackend",
        path: `/stripe/update-subscriptions`,
        options: {
          body: {
            subscriptionId,
            productId: priceId,
          },
        },
      });
      const resp = await updateSubscription.response;
      await resp.body.json();
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await initRevcat();
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await initStripe(true);
      return;
    } catch (error) {
      console.log(error);
    } finally {
      setSubscribing(false);
    }
    return;
  };

  const handleResubscribeSubscription = async (
    customerId: string,
    subscriptionId: string,
  ) => {
    try {
      setSubscribing(true);
      const postResubscribeSubscription = post({
        apiName: "StripeBackend",
        path: `/stripe/resubscribe-subscription`,
        options: {
          body: {
            customerId,
            subscriptionId,
          },
        },
      });
      const resp = await postResubscribeSubscription.response;
      await resp.body.json();
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await initRevcat();
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await initStripe(true);
    } catch (error) {
      console.log(error);
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelSchedule = async () => {
    const scheduledSubscription = scheduledSubscriptions[0];
    setCancellingSchedule(true);
    try {
      const postCancelScheduledSubscription = post({
        apiName: "StripeBackend",
        path: `/stripe/cancel-scheduled-subscription`,
        options: {
          body: {
            customerId: scheduledSubscription.customer,
            scheduleId: scheduledSubscription.id,
          },
        },
      });
      const resp = await postCancelScheduledSubscription.response;
      await resp.body.json();
      await initStripe(true);
    } catch (error) {
      console.log(error);
    } finally {
      setCancellingSchedule(false);
    }
  };

  const renderCancelScheduleButton = () => {
    return (
      <Button
        fw={500}
        h={45}
        color="red"
        onClick={handleCancelSchedule}
        loading={cancellingSchedule}
        disabled={cancellingSchedule}
      >
        Cancel schedule
      </Button>
    );
  };

  const renderSubscribeButtonWhilePromotional = (lookupKey: string) => {
    return (
      <Button
        fw={500}
        h={45}
        onClick={() => handleSubscribe(lookupKey, false, {})}
        loading={subscribing}
        disabled={subscribing}
      >
        Subscribe
      </Button>
    );
  };

  const renderUpcomingInvoiceText = () => {
    return (
      <Group gap={5}>
        <Text
          size="md"
          c={COLORS.primary}
          fw={600}
          style={{ cursor: "pointer" }}
          onClick={() => setUpcomingInvoiceModal(true)}
        >
          Preview upcoming invoice:
        </Text>
        <Text
          size="md"
          c={COLORS.primary}
          fw={600}
          style={{ cursor: "pointer" }}
          onClick={() => setUpcomingInvoiceModal(true)}
        >
          {`$${upcomingInvoice?.amount_due / 100}`}
        </Text>
        <IconArrowRight
          size={16}
          color={COLORS.primary}
          style={{ cursor: "pointer" }}
          onClick={() => setUpcomingInvoiceModal(true)}
        />
      </Group>
    );
  };

  const getFeatureTexts = (limits: any) => {
    return [
      `Up to ${limits.templates} templates`,
      `Up to ${limits.transcriptions} transcriptions`,
      `Up to ${limits.cases} cases`,
    ];
  };

  const isFreePlan =
    !activeStripeSubscription ||
    activeStripeSubscription?.current_period_end < Date.now() / 1000;

  const premiumLookupKey =
    subscriptionType === "yearly"
      ? LOOKUP_KEYS.PREMIUM_YEAR
      : LOOKUP_KEYS.PREMIUM_MONTH;

  const essentialLookupKey =
    subscriptionType === "yearly"
      ? LOOKUP_KEYS.ESSENTIAL_YEAR
      : LOOKUP_KEYS.ESSENTIAL_MONTH;


  return (
    <>
      <Group justify="center" mb="md" w={450} maw={"80%"}>
        <SegmentedControl
          value={subscriptionType}
          fullWidth
          color={COLORS.primary}
          w={"100%"}
          size="lg"
          onChange={(value) =>
            setSubscriptionType(value as "monthly" | "yearly")
          }
          radius="xl"
          data={[
            { label: "Monthly", value: "monthly" },
            { label: "Annual", value: "yearly" },
          ]}
        />
      </Group>
      <Group
        className="box-features"
        style={{ width: "100%" }}
        p="xl"
        gap={"xl"}
        align="flex-start"
        justify="center"
      >
        {!hideFreePlan && (
          <PlanCard
            title="Manta Classic Free"
            subtitle="Limited Usage"
            price="Free To Subscribe"
            features={getFeatureTexts(FREE_LIMITS)}
            isFreePlan={true}
            isCurrentPlan={isFreePlan}
            setOpenedModalCancel={() => null}
          />
        )}
        <PlanCard
          lookupKey={premiumLookupKey}
          title={"Premium"}
          subtitle="Best Value"
          price={subscriptionType === "yearly" ? "$599.99" : "$59.99"}
          priceSubtitle={
            subscriptionType === "yearly" ? "Per Year" : "Per Month"
          }
          features={[
            "Unlimited access to templates",
            "Unlimited transcriptions",
            "Unlimited cases",
          ]}
          isCurrentPlan={isCurrentPlan(premiumLookupKey)}
          setOpenedModalCancel={() => setOpenedModalCancel("Premium")}
          extraTexts={() => {
            const currentPlan = isCurrentPlan(premiumLookupKey);
            const scheduledSubscriptionText =
              renderScheduledSubscriptionText(premiumLookupKey);
            const hasUpcomingInvoice =
              checkHasUpcomingInvoice(premiumLookupKey);
            if (!currentPlan && !hasUpcomingInvoice) {
              return null;
            }
            return (
              <>
                {currentPlan && (
                  <>
                    {renderExtraTexts(premiumLookupKey)}
                    {scheduledSubscriptionText && (
                      <>
                        <Divider my="md" />
                        {scheduledSubscriptionText}
                      </>
                    )}
                  </>
                )}
                {hasUpcomingInvoice && (
                  <>
                    {currentPlan && <Divider my="md" />}
                    {renderUpcomingInvoiceText()}
                  </>
                )}
              </>
            );
          }}
          canceled={
            isCurrentPlan(premiumLookupKey) &&
            activeStripeSubscription?.cancel_at_period_end
          }
          onSubscribe={handleSubscribe}
          renderButton={() => {
            if (
              getActivePromotional(premiumLookupKey) ||
              getActivePromotional(essentialLookupKey)
            ) {
              return renderSubscribeButtonWhilePromotional(premiumLookupKey);
            }
            if (!renderScheduledSubscriptionText(premiumLookupKey)) {
              return null;
            }
            return renderCancelScheduleButton();
          }}
          buttonLoading={subscribing}
        />
        <PlanCard
          lookupKey={essentialLookupKey}
          title={"Essential"}
          subtitle="Best Economy"
          price={subscriptionType === "yearly" ? "$299.99" : "$29.99"}
          priceSubtitle={
            subscriptionType === "yearly" ? "Per Year" : "Per Month"
          }
          features={getFeatureTexts(ESSENTIAL_LIMITS)}
          isCurrentPlan={isCurrentPlan(essentialLookupKey)}
          setOpenedModalCancel={() => setOpenedModalCancel("Essential")}
          extraTexts={() => {
            const currentPlan = isCurrentPlan(essentialLookupKey);
            const scheduledSubscriptionText =
              renderScheduledSubscriptionText(essentialLookupKey);
            const hasUpcomingInvoice =
              checkHasUpcomingInvoice(essentialLookupKey);
            if (!currentPlan && !hasUpcomingInvoice) {
              return null;
            }
            return (
              <>
                {currentPlan && (
                  <>
                    {renderExtraTexts(essentialLookupKey)}
                    {scheduledSubscriptionText && (
                      <>
                        <Divider my="md" />
                        {scheduledSubscriptionText}
                      </>
                    )}
                  </>
                )}
                {hasUpcomingInvoice && (
                  <>
                    {currentPlan && <Divider my="md" />}
                    {renderUpcomingInvoiceText()}
                  </>
                )}
              </>
            );
          }}
          canceled={
            isCurrentPlan(essentialLookupKey) &&
            activeStripeSubscription?.cancel_at_period_end
          }
          onSubscribe={handleSubscribe}
          renderButton={() => {
            if (
              getActivePromotional(essentialLookupKey) ||
              getActivePromotional(premiumLookupKey)
            ) {
              return renderSubscribeButtonWhilePromotional(essentialLookupKey);
            }
            if (!renderScheduledSubscriptionText(essentialLookupKey)) {
              return null;
            }
            return renderCancelScheduleButton();
          }}
          buttonLoading={subscribing}
        />
      </Group>
      {switchPlanConfirmModalData && (
        <SwitchPlanConfirmationModal
          openedModal={!!switchPlanConfirmModalData}
          setOpenedModal={() => setSwitchPlanConfirmModalData(null)}
          switchPlanConfirmModalData={switchPlanConfirmModalData}
          handleSwitchPlan={handleSubscribe}
          products={products}
        />
      )}
      {subscribeWhilePromotionalConfirmModalData && (
        <SubscribeWhilePromotionalConfirmationModal
          openedModal={!!subscribeWhilePromotionalConfirmModalData}
          setOpenedModal={() =>
            setSubscribeWhilePromotionalConfirmModalData(null)
          }
          data={subscribeWhilePromotionalConfirmModalData}
          onSubscribe={handleSubscribe}
        />
      )}

    </>
  );
};

export default PlanFeatures;
